/**
 * The employee-facing bell: list, unread count, mark-read, mark-all-read, and web push subscription
 * management. SELF-SCOPED, DELIBERATELY WITH NO ADMIN PERMISSION ANYWHERE IN THIS FILE — every route
 * here acts on the CALLER's own notifications and the caller's own push subscriptions. The route
 * handler (a later PR) derives `personId` from the verified JWT's actor context, exactly like
 * `payslip.own:read` elsewhere in this codebase, never from a path param, query string, or body
 * field (CLAUDE.md invariant 1: the tenant/identity boundary comes from the verified JWT, not from
 * client-supplied input). See `notification-settings.contract.ts` for the separate, permissioned
 * admin config API this is not — HR/admin decides what gets sent; an employee can only read and
 * acknowledge their own inbox and register/deregister their own browser for push.
 *
 * `pathPrefix` style and DTO-schemas-above-the-router convention taken from
 * `integrations.contract.ts` and `roles.contract.ts`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// The bell
// ---------------------------------------------------------------------------

export const notificationListItemSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  module: z.string(),
  /** Null when no template rendered for this (key, channel) — see `renderNotification()`. */
  title: z.string().nullable(),
  body: z.string().nullable(),
  /** From `resolveHref()` — already a safe, encoded, internal path, or null for no link. */
  href: z.string().nullable(),
  readAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type NotificationListItem = z.infer<typeof notificationListItemSchema>;

export const listNotificationsQuerySchema = paginationQuerySchema.extend({
  unreadOnly: z.coerce.boolean().optional(),
});
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;

export const unreadCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

export const markAllReadResponseSchema = z.object({
  success: z.literal(true),
  /** How many rows were actually touched — 0 is a perfectly normal answer, not an error. */
  count: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------------------
// Web push subscription
// ---------------------------------------------------------------------------

/**
 * One `PushSubscription` object as the browser's Push API returns it: `endpoint` identifies the
 * push service and channel, `p256dh`/`auth` are the keys `web-push` needs to encrypt the payload
 * to this specific subscription. The `PushSubscription` table itself (keyed `(orgId, personId,
 * endpoint)`, with a `platform` discriminator for the future Capacitor native-token case) is schema
 * work for a later PR (plan PR 7) — this is only the wire shape.
 */
export const webPushSubscribeBodySchema = z.object({
  endpoint: z.string().url().max(2048),
  p256dh: z.string().min(1).max(500),
  auth: z.string().min(1).max(500),
  userAgent: z.string().max(500).optional(),
});
export type WebPushSubscribeBody = z.infer<typeof webPushSubscribeBodySchema>;

export const webPushUnsubscribeBodySchema = z.object({
  endpoint: z.string().url().max(2048),
});
export type WebPushUnsubscribeBody = z.infer<typeof webPushUnsubscribeBodySchema>;

export const successResponseSchema = z.object({ success: z.literal(true) });

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const notificationsContract = c.router(
  {
    list: {
      method: 'GET',
      path: '/',
      query: listNotificationsQuerySchema,
      responses: {
        200: paginatedSchema(notificationListItemSchema),
        401: errorBodySchema,
      },
      summary: "The caller's own notifications, newest first",
      metadata: routeMeta(null),
    },
    unreadCount: {
      method: 'GET',
      path: '/unread-count',
      responses: {
        200: unreadCountResponseSchema,
        401: errorBodySchema,
      },
      summary: "The caller's own unread count, for the bell badge",
      metadata: routeMeta(null),
    },
    markRead: {
      method: 'PATCH',
      path: '/:id/read',
      pathParams: z.object({ id: uuidSchema }),
      body: c.noBody(),
      responses: {
        200: successResponseSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "Mark one of the caller's own notifications read",
      metadata: routeMeta(null),
    },
    markAllRead: {
      method: 'POST',
      path: '/read-all',
      body: c.noBody(),
      responses: {
        200: markAllReadResponseSchema,
        401: errorBodySchema,
      },
      summary: "Mark every one of the caller's own unread notifications read",
      metadata: routeMeta(null),
    },
    subscribeWebPush: {
      method: 'POST',
      path: '/web-push/subscribe',
      body: webPushSubscribeBodySchema,
      responses: {
        200: successResponseSchema,
        400: errorBodySchema,
        401: errorBodySchema,
      },
      summary: "Register a browser subscription for the caller's own web push",
      metadata: routeMeta(null),
    },
    unsubscribeWebPush: {
      method: 'DELETE',
      path: '/web-push/subscribe',
      body: webPushUnsubscribeBodySchema,
      responses: {
        200: successResponseSchema,
        401: errorBodySchema,
      },
      summary: "Remove one of the caller's own web push subscriptions",
      metadata: routeMeta(null),
    },
  },
  { pathPrefix: '/notifications' },
);
